import React, { useState, useEffect } from 'react';
import caseService from '../../services/caseService';
import triageService from '../../services/triageService';
import { MessageSquare, ChevronDown, Save, Loader2, Calendar, Clock, AlertTriangle, DollarSign } from 'lucide-react';
import StatusHistoryTimeline from '../StatusHistory/StatusHistoryTimeline';
import { OpposingCounselCompact } from "../OpposingCounselCard";

// Predefined case stages
const CASE_STAGES = [
  { value: '', label: 'Select Stage...' },
  { value: 'intake', label: 'Intake' },
  { value: 'investigation', label: 'Investigation' },
  { value: 'pre_litigation', label: 'Pre-Litigation' },
  { value: 'litigation_filed', label: 'Litigation Filed' },
  { value: 'discovery', label: 'Discovery' },
  { value: 'mediation', label: 'Mediation' },
  { value: 'trial_prep', label: 'Trial Preparation' },
  { value: 'trial', label: 'Trial' },
  { value: 'post_trial', label: 'Post-Trial' },
  { value: 'appeal', label: 'Appeal' },
  { value: 'settlement', label: 'Settlement' },
  { value: 'on_hold', label: 'On Hold' },
  { value: 'closed', label: 'Closed' },
];

const CaseOverview = ({ caseData, onRefresh }) => {
  const [editing, setEditing] = useState(false);
  const [outstandingInvoice, setOutstandingInvoice] = useState(null);

  // Sign-first visibility: a case opened on signature alone must never look
  // identical to a paid one. Surface the outstanding engagement invoice.
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const api = (await import('../../services/api')).default;
        const r = await api.get('/invoices', { params: { case_id: caseData.id, page_size: 10 } });
        const rows = r.data?.invoices || r.data?.items || [];
        const open = rows.find((i) => {
          const s = String(i.status || '').toLowerCase();
          return !['paid', 'cancelled', 'canceled', 'void', 'draft'].includes(s);
        });
        if (alive) setOutstandingInvoice(open || null);
      } catch (e) { /* badge is best-effort */ }
    })();
    return () => { alive = false; };
  }, [caseData.id]);
  const [formData, setFormData] = useState({
    case_name: caseData.case_name,
    practice_area: caseData.practice_area,
    description: caseData.description,
    status: caseData.status,
    responsible_attorney: caseData.responsible_attorney || '',
    responsible_staff: caseData.responsible_staff || '',
    originating_attorney: caseData.originating_attorney || '',
    open_date: caseData.open_date ? caseData.open_date.split('T')[0] : '',
    statute_of_limitations_date: caseData.statute_of_limitations_date ? caseData.statute_of_limitations_date.split('T')[0] : '',
    close_date: caseData.close_date ? caseData.close_date.split('T')[0] : '',
    court_case_number: caseData.court_case_number || '',
  });
  const [saving, setSaving] = useState(false);
  const [assignableUsers, setAssignableUsers] = useState({ attorneys: [], staff: [], all: [] });

  // Case stage and client update state
  const [matterStage, setMatterStage] = useState(caseData.matter_stage || '');
  const [clientUpdate, setClientUpdate] = useState(caseData.client_update || '');
  const [savingUpdate, setSavingUpdate] = useState(false);
  const [updateSaved, setUpdateSaved] = useState(false);
  const [savingStatus, setSavingStatus] = useState(false);

  // Triage deletion confirmation dialog state
  const [showTriageDialog, setShowTriageDialog] = useState(false);
  const [pendingTriageCount, setPendingTriageCount] = useState(0);
  const [pendingStatusChange, setPendingStatusChange] = useState(null);

  // Billing rate state
  const [billingSettings, setBillingSettings] = useState(null);
  const [caseRate, setCaseRate] = useState('');
  const [billingMethod, setBillingMethod] = useState('');

  const BILLING_METHODS = [
    { value: 'hourly', label: 'Hourly' },
    { value: 'flat_fee', label: 'Flat Fee' },
    { value: 'contingency', label: 'Contingency' },
    { value: 'prepaid', label: 'Prepaid Hours' },
    { value: 'hybrid', label: 'Hybrid' },
  ];

  // Quick status change handler
  const handleQuickStatusChange = async (newStatus) => {
    if (newStatus === caseData.status) return;

    // If closing the case, check for pending triage entries
    if (newStatus === 'closed') {
      try {
        const result = await triageService.getPendingCountForCase(caseData.id);
        if (result.pending_count > 0) {
          setPendingTriageCount(result.pending_count);
          setPendingStatusChange(newStatus);
          setShowTriageDialog(true);
          return; // Don't proceed until user confirms
        }
      } catch (err) {
        console.error('Failed to check triage entries:', err);
        // Proceed anyway if check fails
      }
    }

    await performStatusChange(newStatus);
  };

  const performStatusChange = async (newStatus, deleteTriageEntries = false) => {
    try {
      setSavingStatus(true);

      // If user chose to delete triage entries, do that first
      if (deleteTriageEntries) {
        await triageService.deleteAllForCase(caseData.id);
      }

      await caseService.updateCase(caseData.id, { status: newStatus });
      onRefresh();
    } catch (err) {
      console.error('Failed to update status:', err);
      alert('Failed to update case status');
    } finally {
      setSavingStatus(false);
      setShowTriageDialog(false);
      setPendingStatusChange(null);
    }
  };

  const handleTriageDialogConfirm = (deleteEntries) => {
    performStatusChange(pendingStatusChange, deleteEntries);
  };

  useEffect(() => {
    loadAssignableUsers();
    loadBillingSettings();
  }, [caseData.id]);

  const loadBillingSettings = async () => {
    try {
      const response = await fetch(`/api/cases/${caseData.id}/billing`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setBillingSettings(data);
        // Set the case rate - use attorney_rate as the "case rate"
        setCaseRate(data.settings?.attorney_rate?.toString() || '');
        // Set the billing method
        setBillingMethod(data.settings?.billing_method || 'hourly');
      }
    } catch (err) {
      console.error('Failed to load billing settings:', err);
    }
  };

  const loadAssignableUsers = async () => {
    try {
      const response = await fetch('/api/cases/assignable-users/list', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setAssignableUsers(data);
      }
    } catch (err) {
      console.error('Failed to load assignable users:', err);
    }
  };

  // Helper to get display name from email
  const getUserDisplayName = (email) => {
    if (!email) return '— Unassigned —';
    const user = assignableUsers.all.find(u => u.email === email);
    return user ? user.full_name : email;
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      // Clean up empty date strings - send null instead of ''
      const cleanedData = { ...formData };
      ['open_date', 'close_date', 'statute_of_limitations_date'].forEach(field => {
        if (cleanedData[field] === '') {
          cleanedData[field] = null;
        }
      });
      await caseService.updateCase(caseData.id, cleanedData);

      // Save billing settings if changed
      const newRate = parseFloat(caseRate) || null;
      const currentRate = billingSettings?.settings?.attorney_rate || billingSettings?.firm_defaults?.attorney_rate;
      const currentMethod = billingSettings?.settings?.billing_method || 'hourly';

      if ((newRate && newRate !== currentRate) || billingMethod !== currentMethod) {
        await fetch(`/api/cases/${caseData.id}/billing`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            billing_method: billingMethod,
            attorney_rate_override: newRate
          })
        });
      }

      setEditing(false);
      onRefresh();
      loadBillingSettings();
    } catch (err) {
      alert('Failed to update case');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveClientUpdate = async () => {
    try {
      setSavingUpdate(true);
      await caseService.updateCase(caseData.id, {
        matter_stage: matterStage,
        client_update: clientUpdate
      });
      setUpdateSaved(true);
      setTimeout(() => setUpdateSaved(false), 3000);
      onRefresh();
    } catch (err) {
      alert('Failed to save client update');
    } finally {
      setSavingUpdate(false);
    }
  };

  const getStageBadgeColor = (stage) => {
    const colors = {
      intake: 'bg-blue-500/20 text-blue-300 border-blue-500/30',
      investigation: 'bg-cyan-500/20 text-cyan-300 border-cyan-500/30',
      pre_litigation: 'bg-amber-500/20 text-amber-300 border-amber-500/30',
      litigation_filed: 'bg-orange-500/20 text-orange-300 border-orange-500/30',
      discovery: 'bg-cyan-500/20 text-cyan-300 border-cyan-500/30',
      mediation: 'bg-blue-500/20 text-blue-300 border-blue-500/30',
      trial_prep: 'bg-red-500/20 text-red-300 border-red-500/30',
      trial: 'bg-red-600/20 text-red-400 border-red-600/30',
      post_trial: 'bg-indigo-500/20 text-indigo-300 border-indigo-500/30',
      on_hold: 'bg-purple-500/20 text-purple-300 border-purple-500/30',
      appeal: 'bg-violet-500/20 text-violet-300 border-violet-500/30',
      settlement: 'bg-green-500/20 text-green-300 border-green-500/30',
      closed: 'bg-slate-500/20 text-slate-300 border-slate-500/30',
    };
    return colors[stage] || 'bg-slate-500/20 text-slate-300 border-slate-500/30';
  };

  return (
    <div className="p-6 space-y-6">
      {outstandingInvoice && (
        <div className="mb-4 flex items-center gap-2 px-3 py-2 rounded-lg bg-amber-500/15 border border-amber-500/40 text-amber-300 text-sm">
          <DollarSign className="w-4 h-4 shrink-0" />
          <span>
            <strong>Engagement invoice outstanding</strong> — {outstandingInvoice.invoice_number}
            {outstandingInvoice.due_date ? ` · due ${new Date(outstandingInvoice.due_date).toLocaleDateString()}` : ' · unpaid'}
          </span>
        </div>
      )}
      {/* Case Stage & Client Update - Prominent Section */}
      <div className="bg-gradient-to-br from-slate-900/30 to-blue-900/20 border border-cyan-500/30 rounded-xl p-5">
        <div className="flex items-center gap-2 mb-4">
          <MessageSquare className="w-5 h-5 text-cyan-400" />
          <h3 className="text-lg font-semibold text-white">Case Stage & Client Update</h3>
          <span className="text-xs text-cyan-300 ml-2">(Visible to client in portal)</span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Case Stage Dropdown */}
          <div>
            <label className="block text-sm font-medium text-cyan-300 mb-2">Case Stage</label>
            <select
              value={matterStage}
              onChange={(e) => setMatterStage(e.target.value)}
              className="w-full px-3 py-2.5 bg-virgo-navy-700 border border-cyan-500/30 rounded-lg text-white focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
            >
              {CASE_STAGES.map(stage => (
                <option key={stage.value} value={stage.value}>{stage.label}</option>
              ))}
            </select>
            {matterStage && (
              <div className={`mt-2 inline-block px-3 py-1 rounded-full text-xs font-medium border ${getStageBadgeColor(matterStage)}`}>
                {CASE_STAGES.find(s => s.value === matterStage)?.label || matterStage}
              </div>
            )}
          </div>

          {/* Client Update Text */}
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-cyan-300 mb-2">Brief Update for Client</label>
            <textarea
              value={clientUpdate}
              onChange={(e) => setClientUpdate(e.target.value)}
              placeholder="Write a brief update about the case status that the client will see in their portal..."
              rows={3}
              className="w-full px-3 py-2 bg-virgo-navy-700 border border-cyan-500/30 rounded-lg text-white placeholder-virgo-teal-600 focus:ring-2 focus:ring-cyan-500 focus:border-transparent resize-none"
            />
          </div>
        </div>

        {/* Save Button */}
        <div className="flex items-center justify-end mt-4 gap-3">
          {updateSaved && (
            <span className="text-green-400 text-sm flex items-center gap-1">
              ✓ Saved
            </span>
          )}
          <button
            onClick={handleSaveClientUpdate}
            disabled={savingUpdate}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-cyan-500 to-blue-500 rounded-lg hover:from-cyan-600 hover:to-blue-600 disabled:opacity-50 transition-all shadow-lg shadow-cyan-500/20"
          >
            {savingUpdate ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="w-4 h-4" />
                Save Update
              </>
            )}
          </button>
        </div>
      </div>

      {/* Status History Timeline */}
      <StatusHistoryTimeline entityType="case" entityId={caseData.id} />

      {/* Edit Mode Toggle */}
      <div className="flex justify-end">
        {!editing ? (
          <button
            onClick={() => setEditing(true)}
            className="px-4 py-2 text-sm font-medium text-cyan-400 bg-cyan-500/20 border border-cyan-500/30 rounded-lg hover:bg-cyan-500/30 transition-colors"
          >
            Edit Case
          </button>
        ) : (
          <div className="space-x-2">
            <button
              onClick={() => setEditing(false)}
              className="px-4 py-2 text-sm font-medium text-virgo-teal-300 bg-virgo-navy-600 border border-virgo-teal-700/50 rounded-lg hover:bg-virgo-navy-500 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-cyan-500 to-blue-500 rounded-lg hover:from-cyan-600 hover:to-blue-600 disabled:opacity-50 transition-all"
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        )}
      </div>

      {/* Case Information */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-virgo-teal-400 mb-1">Case Name</label>
          {editing ? (
            <input
              type="text"
              value={formData.case_name}
              onChange={(e) => setFormData({ ...formData, case_name: e.target.value })}
              className="w-full px-3 py-2 bg-virgo-navy-600 border border-virgo-teal-700/50 rounded-lg text-white focus:ring-2 focus:ring-cyan-500"
            />
          ) : (
            <p className="text-white">{caseData.case_name}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-virgo-teal-400 mb-1">Practice Area</label>
          {editing ? (
            <input
              type="text"
              value={formData.practice_area}
              onChange={(e) => setFormData({ ...formData, practice_area: e.target.value })}
              className="w-full px-3 py-2 bg-virgo-navy-600 border border-virgo-teal-700/50 rounded-lg text-white focus:ring-2 focus:ring-cyan-500"
            />
          ) : (
            <p className="text-white">{caseData.practice_area || '—'}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-virgo-teal-400 mb-1">Status</label>
          <div className="flex items-center gap-2">
            <select
              value={caseData.status}
              onChange={(e) => handleQuickStatusChange(e.target.value)}
              disabled={savingStatus}
              className={`flex-1 px-3 py-2 bg-virgo-navy-600 border rounded-lg text-white focus:ring-2 focus:ring-cyan-500 ${
                caseData.status === 'active' ? 'border-green-500/50' :
                caseData.status === 'pending' ? 'border-yellow-500/50' :
                caseData.status === 'on_hold' ? 'border-purple-500/50' :
                caseData.status === 'closed' ? 'border-slate-500/50' : 'border-virgo-teal-700/50'
              }`}
            >
              <option value="active">Active</option>
              <option value="pending">Pending</option>
              <option value="on_hold">On Hold</option>
              <option value="closed">Closed</option>
            </select>
            {savingStatus && <Loader2 className="w-4 h-4 animate-spin text-cyan-400" />}
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-virgo-teal-400 mb-1">Court Case Number</label>
          {editing ? (
            <input
              type="text"
              value={formData.court_case_number}
              onChange={(e) => setFormData({ ...formData, court_case_number: e.target.value })}
              placeholder="e.g., 2025-CV-12345"
              className="w-full px-3 py-2 bg-virgo-navy-600 border border-virgo-teal-700/50 rounded-lg text-white focus:ring-2 focus:ring-cyan-500"
            />
          ) : (
            <p className="text-white">{caseData.court_case_number || '—'}</p>
          )}
        </div>

        {/* Billing rollup — full billing/prepaid config lives in the Time & Billing tab (deduped) */}
        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-virgo-teal-400 mb-1 flex items-center gap-1">
            <DollarSign className="w-3 h-3" />
            Billing
          </label>
          <p className="text-white">
            {BILLING_METHODS.find(m => m.value === billingMethod)?.label || billingMethod || 'Hourly'}
            {' · '}
            ${billingSettings?.settings?.attorney_rate || billingSettings?.firm_defaults?.attorney_rate || '—'}/hr
            <span className="ml-2 text-xs text-virgo-teal-500">— manage in the Time &amp; Billing tab</span>
          </p>
        </div>
      </div>

      {/* Case Dates */}
      <div className="pt-6 border-t border-virgo-navy-600">
        <div className="flex items-center gap-2 mb-4">
          <Calendar className="w-5 h-5 text-virgo-gold-400" />
          <h3 className="text-lg font-semibold text-virgo-gold-400">Case Dates</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-virgo-teal-400 mb-1">Open Date</label>
            {editing ? (
              <input
                type="date"
                value={formData.open_date}
                onChange={(e) => setFormData({ ...formData, open_date: e.target.value })}
                className="w-full px-3 py-2 bg-virgo-navy-600 border border-virgo-teal-700/50 rounded-lg text-white focus:ring-2 focus:ring-cyan-500"
              />
            ) : (
              <p className="text-white">
                {caseData.open_date
                  ? new Date(caseData.open_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                  : '— Not set —'}
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-virgo-teal-400 mb-1">Statute of Limitations</label>
            {editing ? (
              <input
                type="date"
                value={formData.statute_of_limitations_date}
                onChange={(e) => setFormData({ ...formData, statute_of_limitations_date: e.target.value })}
                className="w-full px-3 py-2 bg-virgo-navy-600 border border-virgo-teal-700/50 rounded-lg text-white focus:ring-2 focus:ring-cyan-500"
              />
            ) : (
              <p className={`${caseData.statute_of_limitations_date ? 'text-amber-400 font-medium' : 'text-white'}`}>
                {caseData.statute_of_limitations_date
                  ? new Date(caseData.statute_of_limitations_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                  : '— Not set —'}
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-virgo-teal-400 mb-1">Close Date</label>
            {editing ? (
              <input
                type="date"
                value={formData.close_date}
                onChange={(e) => setFormData({ ...formData, close_date: e.target.value })}
                className="w-full px-3 py-2 bg-virgo-navy-600 border border-virgo-teal-700/50 rounded-lg text-white focus:ring-2 focus:ring-cyan-500"
              />
            ) : (
              <p className="text-white">
                {caseData.close_date
                  ? new Date(caseData.close_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                  : '— Not closed —'}
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-virgo-teal-400 mb-1 flex items-center gap-1">
              <Clock className="w-3 h-3" />
              Created in System
            </label>
            <p className="text-virgo-teal-300 text-sm">
              {caseData.created_at
                ? new Date(caseData.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                : '— Unknown —'}
            </p>
          </div>
        </div>
      </div>

      {/* Team Assignment */}
      <div className="pt-6 border-t border-virgo-navy-600">
        <h3 className="text-lg font-semibold text-virgo-gold-400 mb-4">Case Team</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-virgo-teal-400 mb-1">Responsible Attorney</label>
            {editing ? (
              <select
                value={formData.responsible_attorney}
                onChange={(e) => setFormData({ ...formData, responsible_attorney: e.target.value })}
                className="w-full px-3 py-2 bg-virgo-navy-600 border border-virgo-teal-700/50 rounded-lg text-white focus:ring-2 focus:ring-cyan-500"
              >
                <option value="">— Unassigned —</option>
                {assignableUsers.attorneys.map(user => (
                  <option key={user.id} value={user.email}>{user.full_name}</option>
                ))}
              </select>
            ) : (
              <p className="text-white">{getUserDisplayName(caseData.responsible_attorney)}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-virgo-teal-400 mb-1">Originating Attorney</label>
            {editing ? (
              <select
                value={formData.originating_attorney}
                onChange={(e) => setFormData({ ...formData, originating_attorney: e.target.value })}
                className="w-full px-3 py-2 bg-virgo-navy-600 border border-virgo-teal-700/50 rounded-lg text-white focus:ring-2 focus:ring-cyan-500"
              >
                <option value="">— Unassigned —</option>
                {assignableUsers.attorneys.map(user => (
                  <option key={user.id} value={user.email}>{user.full_name}</option>
                ))}
              </select>
            ) : (
              <p className="text-white">{getUserDisplayName(caseData.originating_attorney)}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-virgo-teal-400 mb-1">Responsible Staff</label>
            {editing ? (
              <select
                value={formData.responsible_staff}
                onChange={(e) => setFormData({ ...formData, responsible_staff: e.target.value })}
                className="w-full px-3 py-2 bg-virgo-navy-600 border border-virgo-teal-700/50 rounded-lg text-white focus:ring-2 focus:ring-cyan-500"
              >
                <option value="">— Unassigned —</option>
                {assignableUsers.staff.map(user => (
                  <option key={user.id} value={user.email}>{user.full_name}</option>
                ))}
              </select>
            ) : (
              <p className="text-white">{getUserDisplayName(caseData.responsible_staff)}</p>
            )}
          </div>
        </div>
      </div>

      {/* Description */}
      <div>
        <label className="block text-sm font-medium text-virgo-teal-400 mb-1">Description</label>
        {editing ? (
          <textarea
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            rows={4}
            className="w-full px-3 py-2 bg-virgo-navy-600 border border-virgo-teal-700/50 rounded-lg text-white focus:ring-2 focus:ring-cyan-500"
          />
        ) : (
          <p className="text-virgo-teal-200 whitespace-pre-wrap">{caseData.description || 'No description'}</p>
        )}
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 pt-6 border-t border-virgo-navy-600">
        <div className="text-center p-4 bg-gradient-to-br from-blue-500/20 to-cyan-500/20 border border-blue-500/30 rounded-xl">
          <p className="text-3xl font-bold text-white">{caseData.document_count}</p>
          <p className="text-sm text-blue-300 mt-1">Documents</p>
        </div>
        <div className="text-center p-4 bg-gradient-to-br from-green-500/20 to-emerald-500/20 border border-green-500/30 rounded-xl">
          <p className="text-3xl font-bold text-white">
            {caseData.time_summary?.total_hours?.toFixed(1) || 0}
          </p>
          <p className="text-sm text-green-300 mt-1">Hours Logged</p>
        </div>
        <div className="text-center p-4 bg-gradient-to-br from-cyan-500/20 to-blue-500/20 border border-cyan-500/30 rounded-xl">
          <p className="text-3xl font-bold text-white">{caseData.task_count}</p>
          <p className="text-sm text-cyan-300 mt-1">Open Tasks</p>
        </div>
      </div>

      {/* Client Contact */}
      {caseData.client_contact && (
        <div className="pt-6 border-t border-virgo-navy-600">
          <h3 className="text-lg font-semibold text-virgo-gold-400 mb-4">Client Information</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-virgo-navy-800/50 rounded-lg p-4">
            <div>
              <label className="block text-sm font-medium text-virgo-teal-400 mb-1">Name</label>
              <p className="text-white">{caseData.client_contact.contact_name}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-virgo-teal-400 mb-1">Email</label>
              <p className="text-white">{caseData.client_contact.primary_email_address || '—'}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-virgo-teal-400 mb-1">Phone</label>
              <p className="text-white">{caseData.client_contact.primary_phone_number || '—'}</p>
            </div>
          </div>
        </div>
      )}

      {/* Triage Entries Confirmation Dialog */}
      {showTriageDialog && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-virgo-navy-800 border border-amber-500/30 rounded-xl p-6 max-w-md mx-4 shadow-xl">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-amber-500/20 rounded-lg">
                <AlertTriangle className="w-6 h-6 text-amber-400" />
              </div>
              <h3 className="text-lg font-semibold text-white">Pending Triage Items</h3>
            </div>

            <p className="text-virgo-teal-200 mb-6">
              This case has <span className="font-semibold text-amber-400">{pendingTriageCount}</span> pending
              triage {pendingTriageCount === 1 ? 'item' : 'items'}. Would you like to delete {pendingTriageCount === 1 ? 'it' : 'them'} when
              closing the case?
            </p>

            <div className="flex flex-col gap-2">
              <button
                onClick={() => handleTriageDialogConfirm(true)}
                disabled={savingStatus}
                className="w-full px-4 py-2.5 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors disabled:opacity-50"
              >
                {savingStatus ? 'Processing...' : 'Yes, Delete Triage Items'}
              </button>
              <button
                onClick={() => handleTriageDialogConfirm(false)}
                disabled={savingStatus}
                className="w-full px-4 py-2.5 text-sm font-medium text-white bg-virgo-navy-600 hover:bg-virgo-navy-500 border border-virgo-teal-700/50 rounded-lg transition-colors disabled:opacity-50"
              >
                {savingStatus ? 'Processing...' : 'No, Keep Triage Items'}
              </button>
              <button
                onClick={() => {
                  setShowTriageDialog(false);
                  setPendingStatusChange(null);
                }}
                disabled={savingStatus}
                className="w-full px-4 py-2 text-sm text-virgo-teal-400 hover:text-white transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Opposing Counsel Intel */}
      <OpposingCounselCompact caseId={caseData?.id} />
    </div>
  );
};

export default CaseOverview;
